import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { usePresence } from '../../context/PresenceContext';
import {
  getProfileCompletionPath,
  needsProfilePhotoCompletion,
} from '../../utils/profileCompletion';
import axiosConn from '../../axiosConn';
import './orbitingFriends.css';

const STATUS_COLORS = {
  online: '#00e676', // green
  idle: '#ffee58', // yellow
  away: '#ff9800', // orange
  dnd: '#f44336', // red
  offline: '#9e9e9e',
};

export default function OrbitingFriends() {
  const history = useHistory();
  const { player } = useContext(AuthContext);
  const presenceCtx = usePresence() || {};
  const presenceMap = presenceCtx.presenceMap || {};
  const [friends, setFriends] = useState([]);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true,
  );
  const orbitTimingRef = useRef(new Map());
  const offsetRetryRef = useRef();

  const selfPlayer = player?.data?.player || null;
  const selfId = selfPlayer?._id || null;
  const showProfilePhotoNudge = needsProfilePhotoCompletion(player);
  const profilePhotoNudgePath = getProfileCompletionPath(player, {
    focus: showProfilePhotoNudge,
  });

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const resolvePresence = (pm, friend) => {
    if (!pm || !friend) return null;

    const keysToTry = [
      friend.playerId && String(friend.playerId),
      friend.accountId && String(friend.accountId),
      friend._id && String(friend._id),
    ].filter(Boolean);

    for (const k of keysToTry) {
      if (pm[k]) return pm[k];
    }
    return null;
  };

  const getFriendKey = (friend) =>
    friend?.playerId ||
    friend?._id ||
    friend?.accountId ||
    friend?.id ||
    friend?.username;

  useEffect(() => {
    if (!selfId || !player?.token) return;

    const ac = new AbortController();

    const loadFriends = async () => {
      try {
        const { data } = await axiosConn.get(`players/friends/${selfId}`, {
          headers: { Authorization: `Bearer ${player.token}` },
          signal: ac.signal,
        });

        const raw = Array.isArray(data)
          ? data
          : Array.isArray(data?.friends)
          ? data.friends
          : [];

        const list = raw.filter((f) => f && f._id);
        if (list.length !== raw.length) {
          console.warn('[OrbitingFriends] filtered stale/null friends', {
            requested: raw.length,
            kept: list.length,
          });
        }

        setFriends(list);
      } catch (err) {
        const isAbort =
          err?.name === 'AbortError' ||
          err?.name === 'CanceledError' ||
          err?.code === 'ERR_CANCELED';
        if (!isAbort) {
          console.error('[OrbitingFriends] Failed loading friends:', err);
        }
      }
    };

    loadFriends();
    return () => ac.abort();
  }, [selfId, player?.token]);

  // All friends who have any presence (including offline)
  const presenceEntries = useMemo(() => {
    if (!friends || friends.length === 0) return [];
    const pm = presenceMap || {};
    const seen = new Set();

    const rank = (status) => {
      if (status === 'online') return 0;
      if (status === 'idle') return 1;
      if (status === 'away') return 2;
      if (status === 'dnd') return 3;
      return 4;
    };

    return friends
      .map((f) => {
        if (!f) return null;
        const key = getFriendKey(f);
        if (!key || seen.has(key)) return null;
        const presence = resolvePresence(pm, f);
        if (!presence) return null;
        seen.add(key);
        return { friend: f, presence, key };
      })
      .filter(Boolean)
      .sort((a, b) => {
        const sa = a.presence?.status;
        const sb = b.presence?.status;
        const ra = rank(sa);
        const rb = rank(sb);
        if (ra !== rb) return ra - rb;

        const la = a.presence?.latencyMs ?? 9999;
        const lb = b.presence?.latencyMs ?? 9999;
        return la - lb;
      });
  }, [friends, presenceMap]);

  // Online-ish (non-offline or within offline grace) used for counts / tray
  const onlineFriends = useMemo(
    () =>
      presenceEntries.filter((entry) => {
        const status = entry.presence?.status;
        if (!status) return false;
        if (status !== 'offline') return true;
        const offAt =
          entry.presence?.wentOfflineAt || entry.presence?.lastSeen || null;
        return offAt && Date.now() - offAt <= 3 * 60 * 60 * 1000;
      }),
    [presenceEntries],
  );

  // Hide self from orbit ring
  const orbitEntries = useMemo(() => {
    if (!selfId) return presenceEntries;
    const selfIdStr = String(selfId);

    return presenceEntries.filter((entry) => {
      const f = entry.friend;
      const ids = [
        f?._id && String(f._id),
        f?.accountId && String(f.accountId),
        f?.playerId && String(f.playerId),
      ].filter(Boolean);

      return !ids.includes(selfIdStr);
    });
  }, [presenceEntries, selfId]);

  const orbitAngles = useMemo(() => {
    const keys = orbitEntries
      .map((e) => e.key || getFriendKey(e.friend))
      .filter(Boolean);
    const unique = Array.from(new Set(keys)).sort();
    const step = unique.length ? 360 / unique.length : 0;
    const map = new Map();
    unique.forEach((k, idx) => map.set(k, step * idx));
    return map;
  }, [orbitEntries]);

  const getOrbitDurationMs = (status) => {
    if (status === 'online') return 12000;
    if (status === 'idle') return 20000;
    return 40000;
  };

  const computeOrbitTiming = useCallback(
    (friendKey, status) => {
      if (typeof window === 'undefined') {
        const durationMs = getOrbitDurationMs(status);
        return { durationMs, delayMs: 0 };
      }

      const durationMs = getOrbitDurationMs(status);
      const now =
        typeof performance !== 'undefined' && performance.now
          ? performance.now()
          : Date.now();

      const map = orbitTimingRef.current;
      const prev = map.get(friendKey);
      if (!prev) {
        map.set(friendKey, { start: now, durationMs });
        return { durationMs, delayMs: 0 };
      }

      const prevDuration = prev.durationMs || durationMs;
      const elapsed = now - prev.start;
      const progress =
        prevDuration > 0 ? (elapsed % prevDuration) / prevDuration : 0;
      const alignedStart = now - progress * durationMs;
      const delayMs = -progress * durationMs;

      map.set(friendKey, { start: alignedStart, durationMs });
      return { durationMs, delayMs };
    },
    [],
  );

  const handleClick = useCallback(
    (f) => {
      if (f?.playerId) {
        history.push(`/profile/${f.playerId}`);
      }
    },
    [history],
  );

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const rootEl = document.documentElement;
    const bodyEl = document.body;

    if (!selfPlayer || !isDesktop) {
      bodyEl.classList.remove('orbiting-friends-active');
      rootEl.style.removeProperty('--orbiting-friends-offset');
      rootEl.style.removeProperty('--orbiting-friends-top');
      return undefined;
    }

    bodyEl.classList.add('orbiting-friends-active');

    return () => {
      bodyEl.classList.remove('orbiting-friends-active');
      rootEl.style.removeProperty('--orbiting-friends-offset');
      rootEl.style.removeProperty('--orbiting-friends-top');
    };
  }, [selfPlayer, isDesktop]);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!selfPlayer || !isDesktop) return undefined;

    const rootEl = document.documentElement;

    const clearRetry = () => {
      if (offsetRetryRef.current) {
        clearTimeout(offsetRetryRef.current);
        offsetRetryRef.current = null;
      }
    };

    const computeOffsets = () => {
      const navEl =
        document.querySelector('.trailblazer-nav') ||
        document.querySelector('.legacy-nav') ||
        document.querySelector('.topbarNav') ||
        document.querySelector('.topbarContainer');
      const shellEl = document.querySelector('.orbiting-friends-root');

      const navHeight = navEl?.getBoundingClientRect().height || 0;
      const shellRect = shellEl?.getBoundingClientRect();

      const orbitTop = Math.max(navHeight + 12, shellRect?.top ?? 0);
      const orbitHeight = shellRect?.height ?? 240;
      const orbitBottom = shellRect?.bottom ?? orbitTop + orbitHeight;
      const offset = Math.max(orbitHeight, Math.max(0, orbitBottom - navHeight));

      rootEl.style.setProperty(
        '--orbiting-friends-top',
        `${Math.round(orbitTop)}px`,
      );
      rootEl.style.setProperty(
        '--orbiting-friends-offset',
        `${Math.round(offset)}px`,
      );

      // Retry once layout has real dimensions (e.g., slow presence load)
      if (!shellRect || orbitHeight <= 20) {
        clearRetry();
        offsetRetryRef.current = setTimeout(computeOffsets, 300);
      }
    };

    computeOffsets();
    const raf = requestAnimationFrame(computeOffsets);
    window.addEventListener('resize', computeOffsets);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', computeOffsets);
      clearRetry();
    };
  }, [selfPlayer, isDesktop, orbitEntries.length]);

  if (!selfPlayer) return null;

  return (
    <div className="orbiting-friends-shell">
      <div className="orbiting-friends-root">
        <div className="orbiting-friends-center">
          <div className="orbiting-friends-center-ring">
            {selfPlayer.profilePhoto ? (
              <img
                src={selfPlayer.profilePhoto}
                className="orbiting-friends-center-avatar"
                alt="you"
                draggable="false"
              />
            ) : (
              <div className="orbiting-friends-center-avatar-fallback">YOU</div>
            )}
          </div>
          <div className="orbiting-friends-center-bar" />
          <div className="orbiting-friends-center-label">YOU</div>
          {showProfilePhotoNudge ? (
            <Link
              to={profilePhotoNudgePath}
              className="orbiting-friends-photo-nudge"
              aria-label="Add profile photo and earn 4 JGK"
            >
              Add photo <strong>+4 JGK</strong>
            </Link>
          ) : null}
        </div>

        {orbitEntries.length > 0 && (
          <div className="orbiting-friends-orbit">
            {orbitEntries.map((entry, idx) => {
              const { friend, presence } = entry;
              const friendKey = entry.key || getFriendKey(friend) || `${idx}`;
              const angle =
                orbitAngles.get(friendKey) ??
                (360 / orbitEntries.length) * idx;

              const status = presence?.status || 'offline';
              const orbitStatus =
                status === 'online'
                  ? 'online'
                  : status === 'idle'
                  ? 'idle'
                  : 'offline';
              const { durationMs, delayMs } = computeOrbitTiming(
                friendKey,
                orbitStatus,
              );

              let ringColor;
              if (status === 'online') {
                ringColor = '#00e676';
              } else if (status === 'idle') {
                ringColor = '#ffee58';
              } else if (status === 'dnd') {
                ringColor = '#ff5252';
              } else {
                ringColor = '#9e9e9e';
              }

              const isOfflineGhost = status === 'offline';
              const isOnline = status === 'online';
              const nodeClassName = [
                'orbiting-friend-node',
                isOfflineGhost && 'orbiting-friend-node--offline',
                'of-orbit-avatar',
                `of-status-${orbitStatus}`,
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <button
                  key={friendKey}
                  className={nodeClassName}
                  style={{ '--orbit-angle': `${angle}deg` }}
                  onClick={() => handleClick(friend)}
                  type="button"
                >
                  <div
                    className={
                      'orbiting-friend-ring' +
                      (isOnline ? ' orbiting-friend-ring--online' : '') +
                      (isOfflineGhost ? ' orbiting-friend-ring--offline' : '')
                    }
                    style={{
                      '--orbit-ring-color': ringColor,
                      '--of-animation-duration': `${durationMs}ms`,
                      '--of-animation-delay': `${delayMs}ms`,
                    }}
                  >
                    {friend.profilePhoto ? (
                      <img
                        src={friend.profilePhoto}
                        className="orbiting-friend-avatar"
                        alt={friend.playerId}
                        draggable="false"
                      />
                    ) : (
                      <div className="orbiting-friend-avatar-fallback">
                        {friend.playerId?.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
